import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BirthRegistration, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const REGISTRAR_ROLES = ['REGISTRAR', 'DEPUTY_REGISTRAR'];

// Same 27 districts m01-population validates districtOfResidence against
// (packages/modules/m01-population/src/service/districts.ts).
const DISTRICTS = [
  'anse-aux-pins', 'anse-boileau', 'anse-etoile', 'anse-royale', 'au-cap', 'baie-lazare', 'baie-sainte-anne',
  'beau-vallon', 'bel-air', 'bel-ombre', 'cascade', 'english-river', 'glacis', 'grand-anse-mahe',
  'grand-anse-praslin', 'la-digue', 'la-riviere-anglaise', 'les-mamelles', 'mont-buxton', 'mont-fleuri',
  'plaisance', 'pointe-larue', 'port-glaud', 'roche-caiman', 'saint-louis', 'takamaka', 'victoria',
];

/** Civil Registry — the registrar console queue for parent-filed birth registrations (M01). */
export function Births() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m01/births?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<BirthRegistration>>(path);

  return (
    <div>
      <PageHeader
        title="Births Queue"
        subtitle="Parent-filed birth registrations awaiting claim, review, and NIN issuance (M01)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-48" placeholder="Mother NIN or surname…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All states</option>
              <option value="NOTIFIED">Notified</option>
              <option value="PARENT_COMPLETED">Parent completed</option>
              <option value="REGISTRAR_REVIEW">Registrar review</option>
              <option value="REGISTERED">Registered</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>
        }
      />

      {!isRegistrar ? <RoleNotice roles={REGISTRAR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No birth registrations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Child</th>
                <th className="table-head">Mother NIN</th>
                <th className="table-head">Child DOB</th>
                <th className="table-head">Place of birth</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((b) => (
                <tr key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(b.id)}>
                  <td className="table-cell font-medium">
                    {b.childGivenNames ? `${b.childGivenNames} ${b.childFamilyName ?? ''}` : <span className="text-slate-400">Not yet named</span>}
                  </td>
                  <td className="table-cell font-mono text-xs">{b.motherNin}</td>
                  <td className="table-cell">{formatDate(b.childDob)}</td>
                  <td className="table-cell text-xs text-slate-500">{b.childPlaceOfBirth}</td>
                  <td className="table-cell">
                    <StatusBadge status={b.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <BirthDetailModal
          id={selectedId}
          canAct={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function BirthDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const birthQ = useApiQuery<BirthRegistration>(`/api/v1/m01/births/${id}`);
  const b = birthQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [district, setDistrict] = useState('');
  const [nationality, setNationality] = useState('Seychellois');

  const claim = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m01/births/${id}/claim`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to claim');
    } finally {
      setBusy(false);
    }
  };

  const register = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m01/births/${id}/register`, {
        ...(district ? { districtOfResidence: district } : {}),
        nationality: nationality
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean),
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m01/births/${id}/reject`, { reason: rejectReason });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to reject');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={b?.childGivenNames ? `${b.childGivenNames} ${b.childFamilyName ?? ''}` : 'Birth registration'} onClose={onClose} wide>
      {birthQ.error ? <ErrorNote message={birthQ.error} /> : null}
      {birthQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {b ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={b.state} />} />
            <Detail label="Child sex" value={b.childSex} />
            <Detail label="Child DOB" value={formatDate(b.childDob)} />
            <Detail label="Place of birth" value={b.childPlaceOfBirth} />
            <Detail label="Mother NIN" value={b.motherNin} mono />
            <Detail label="Mother name" value={b.motherName ?? '—'} />
            {b.fatherNin ? <Detail label="Father NIN" value={b.fatherNin} mono /> : null}
            {b.fatherName ? <Detail label="Father name" value={b.fatherName} /> : null}
            <Detail label="Informant" value={b.informantName ? `${b.informantName} (${b.informantRelation})` : '—'} />
            <Detail label="Informant contact" value={b.informantContact ?? '—'} />
          </div>

          {b.state === 'REJECTED' && b.rejectionReason ? <ErrorNote message={`Rejected: ${b.rejectionReason}`} /> : null}
          {b.state === 'REGISTERED' ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
              Registered — civil record {b.civilRecordId}, certificate serial {b.certSerial}.
            </div>
          ) : null}

          {canAct && b.state === 'PARENT_COMPLETED' ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              <button className="btn-primary" disabled={busy} onClick={claim}>
                Claim for review
              </button>
              <button className="btn-danger" disabled={busy} onClick={() => setShowReject((s) => !s)}>
                Reject
              </button>
            </div>
          ) : null}

          {canAct && b.state === 'REGISTRAR_REVIEW' ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="District of residence (optional — defaults to mother's)">
                  <select className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)}>
                    <option value="">Use mother's district</option>
                    {DISTRICTS.map((d) => (
                      <option key={d} value={d}>
                        {d.replaceAll('-', ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Nationality (comma separated)">
                  <input className="field-input" value={nationality} onChange={(e) => setNationality(e.target.value)} />
                </Field>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button className="btn-success" disabled={busy} onClick={register}>
                  {busy ? 'Registering…' : 'Register birth — mint NIN & certificate'}
                </button>
                <button className="btn-danger" disabled={busy} onClick={() => setShowReject((s) => !s)}>
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {showReject ? (
            <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
              <Field label="Rejection reason">
                <textarea className="field-input" rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={reject}>
                Confirm reject
              </button>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Filed {formatDateTime(b.createdAt)} · last updated {formatDateTime(b.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
